'use client'

import { useState } from 'react'
import { Animation, Cell, Divider, EmptyState, Loader, Spacer, Text } from '@vinted/web-ui'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { InfiniteScroll } from '@marketplace-web/common-components/infinite-scroll-ui'
import { useAsset } from '@marketplace-web/shared/assets'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import { FeedbackFilterType } from '@marketplace-web/profile/profile-feedback-data'

import { InfoBanner } from '@marketplace-web/info-banner/info-banner-feature'
import { InfoBannerScreen } from '@marketplace-web/info-banner/info-banner-data'

import FeedbackFilters from './FeedbackFilters'
import FeedbackItem from './FeedbackItem'
import { useFeedbackContext } from './FeedbackProvider'
import FeedbackSummary from './FeedbackSummary'

type Props = {
  userId: number
  username: string
  isContentTranslationEnabled: boolean
  showTitle: boolean
  viewingSelf: boolean
}

const ProfileFeedback = ({
  userId,
  username,
  isContentTranslationEnabled,
  showTitle,
  viewingSelf = false,
}: Props) => {
  const translate = useTranslate('user.feedback')
  const asset = useAsset('/assets/animations')
  const breakpoints = useBreakpoint()

  const { ids, setIds, uiState, showFilters, resetCurrentPage, getEndReached, fetchFeedback } =
    useFeedbackContext()

  const endReached = getEndReached()

  const [selectedFeedbackFilterType, setSelectedFeedbackFilterType] = useState(
    FeedbackFilterType.All,
  )

  const getFeedback = () => {
    if (endReached) return undefined

    return fetchFeedback(userId, selectedFeedbackFilterType)
  }

  const renderFeedbackItem = (id: number) => (
    <FeedbackItem
      key={id}
      id={id}
      userId={userId}
      isContentTranslationEnabled={isContentTranslationEnabled}
    />
  )

  const renderFeedbackItems = () => {
    const separator = (
      <div className="u-ui-margin-vertical-medium">
        <Divider />
      </div>
    )

    return <SeparatedList separator={separator}>{ids.map(renderFeedbackItem)}</SeparatedList>
  }

  const renderEmptyState = () => {
    if (uiState === UiState.Pending || !endReached || ids.length) return null

    return (
      <EmptyState
        animation={<Animation animationUrl={asset('performance-empty-state.json')} />}
        title={translate('empty_state.title')}
        body={translate('empty_state.body')}
      />
    )
  }

  const renderLoader = () => {
    if (uiState !== UiState.Pending) return null

    return (
      <div className="u-flexbox u-justify-content-center">
        <Loader size="large" />
      </div>
    )
  }

  const handleFilterChipClicked = (feedbackFilterType: FeedbackFilterType) => {
    setSelectedFeedbackFilterType(feedbackFilterType)

    resetCurrentPage()
    setIds([])
  }

  const renderFeedbackContent = () => {
    return (
      <Cell>
        <InfiniteScroll
          renderItems={renderFeedbackItems}
          onPageEnd={getFeedback}
          cursor={`feedback-${ids.length}`}
        />
        {renderLoader()}
      </Cell>
    )
  }

  const renderInfoBanner = () => {
    const target = 'merge_reviews_banner'
    if (breakpoints.tabletsUp) {
      return (
        <InfoBanner
          screen={InfoBannerScreen.CurrentUserReviews}
          linkTracking={{ target }}
          theme="inverseExperimental"
        />
      )
    }

    return (
      <div className="u-ui-margin-large">
        <InfoBanner
          screen={InfoBannerScreen.CurrentUserReviews}
          linkTracking={{ target }}
          theme="inverseExperimental"
        />
      </div>
    )
  }

  const renderHeader = () => {
    return (
      <>
        {viewingSelf && renderInfoBanner()}
        {showTitle && (
          <Text as="h1" type="heading">
            {translate('title', { username })}
          </Text>
        )}
      </>
    )
  }

  return (
    <SeparatedList separator={<Spacer size="large" />}>
      {renderHeader()}
      <FeedbackSummary userId={userId} />
      <FeedbackFilters
        selectedFeedbackFilterType={selectedFeedbackFilterType}
        showFilters={showFilters}
        onClick={handleFilterChipClicked}
      />
      {renderEmptyState() || renderFeedbackContent()}
    </SeparatedList>
  )
}

export default ProfileFeedback
